import { SwHeightData, SwHeightSettings, WellLogs } from '../../@types/swHeight/types';

/*
 * What the page was showing, kept across a route change.
 *
 * All of `SwHeightContent`'s state lives in `useState`, so navigating to Kr
 * Analysis and back used to unmount it, refetch, and lose any calibration the
 * user had not saved. Holding it here means leaving and returning is instant and
 * the unsaved work survives.
 *
 * **Module-level and in-memory on purpose.** A reload empties it, and the page
 * falls back to the stored calibration -- which is the intended behaviour, not a
 * limitation: an unsaved experiment should not outlive the session that made it.
 * `sessionStorage` would resurrect one, and would also mean serialising several
 * hundred KB of curve arrays on every keystroke.
 *
 * Keyed by project uid, so two projects opened in turn do not see each other's
 * work. Entries are replaced wholesale rather than merged -- a partial entry
 * would be a half-loaded page.
 */

export interface SwHeightCacheEntry {
  data: SwHeightData | null;
  /** Core-plug rows, held for their measured lab Sw only. */
  coreLogs: WellLogs | null;
  /**
   * The last settings known to be stored server-side. `null` when nothing has
   * been saved for this project yet, which is also what makes the first compute
   * persist itself.
   */
  savedSettings: SwHeightSettings | null;
  hasLogs: boolean;
}

const cache = new Map<string, SwHeightCacheEntry>();

export const readSwHeightCache = (
  projectUid: string
): SwHeightCacheEntry | undefined => cache.get(projectUid);

export const writeSwHeightCache = (
  projectUid: string,
  entry: SwHeightCacheEntry
): void => {
  cache.set(projectUid, entry);
};

/**
 * Update part of a cached entry, if there is one.
 *
 * A no-op when nothing is cached: every caller is reacting to a state change on
 * a page that must already have loaded, so an absent entry means the page was
 * torn down mid-flight and there is nothing to keep.
 */
export const patchSwHeightCache = (
  projectUid: string,
  changes: Partial<SwHeightCacheEntry>
): void => {
  const existing = cache.get(projectUid);
  if (!existing) return;
  cache.set(projectUid, { ...existing, ...changes });
};

/** Drop an entry after an import, a clear or a seed has changed the stored data. */
export const invalidateSwHeightCache = (projectUid: string): void => {
  cache.delete(projectUid);
};

/** Test seam. Never call this from the app -- entries are per project already. */
export const clearSwHeightCache = (): void => {
  cache.clear();
};
